import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, X, Save } from 'lucide-react';
import { userInputToUTC } from '../utils/timezone';
import axios from 'axios';
import TimezoneDisplay from '../components/TimezoneDisplay';

const CreateEvent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [storytellers, setStorytellers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    timezone: 'America/New_York',
    duration: 3, // Default 3 hours
    location: '',
    storytellerIds: [],
    createDiscordEvent: true
  });

  // Helper function to get next occurrence of a specific day of week
  const getNextOccurrence = (dayOfWeek) => {
    const today = new Date();
    const targetDay = dayOfWeek; // 0 = Sunday, 3 = Wednesday, 5 = Friday, 6 = Saturday
    const currentDay = today.getDay();
    
    let daysUntilTarget = targetDay - currentDay;
    if (daysUntilTarget <= 0) {
      daysUntilTarget += 7; // Next week
    }
    
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilTarget);
    return nextDate;
  };

  // Quick select handlers
  const handleQuickSelect = (dayOfWeek, dayName) => {
    const nextDate = getNextOccurrence(dayOfWeek);
    
    // Create date string in user's local timezone WITHOUT converting to UTC
    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Generate title if not already set
    const generatedTitle = formData.title || `${dayName} Blood on the Clocktower`;
    
    setFormData(prev => ({
      ...prev,
      date: dateStr,
      time: '20:30', // 8:30 PM
      timezone: 'America/New_York',
      title: generatedTitle
    }));
  };

  // Handle today selection
  const handleTodaySelect = () => {
    const today = new Date();
    
    // Create date string in user's local timezone WITHOUT converting to UTC
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Generate title if not already set
    const generatedTitle = formData.title || `${dayName} Blood on the Clocktower`;
    
    setFormData(prev => ({
      ...prev,
      date: dateStr,
      time: '20:30', // 8:30 PM
      timezone: 'America/New_York',
      title: generatedTitle
    }));
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dayOfWeek) => {
    const nextDate = getNextOccurrence(dayOfWeek);
    return nextDate.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    fetchStorytellers();
  }, []);

  const fetchStorytellers = async () => {
    try {
      const response = await axios.get('/api/users/storytellers/list');
      setStorytellers(response.data);
    } catch (error) {
      console.error('Failed to fetch storytellers:', error);
    }
  };

  // Handle manual time input
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      };
      
      // Auto-generate title when date/time is set and title is empty
      if ((name === 'date' || name === 'time') && !prev.title) {
        if (newFormData.date && newFormData.time) {
          try {
            const eventDate = new Date(`${newFormData.date}T${newFormData.time}`);
            const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
            newFormData.title = `${dayName} Blood on the Clocktower`;
          } catch (error) {
            console.error('Error generating title:', error);
          }
        }
      }
      
      return newFormData;
    });
  };

  const handleStorytellerToggle = (storytellerId) => {
    setFormData(prev => ({
      ...prev,
      storytellerIds: prev.storytellerIds.includes(storytellerId)
        ? prev.storytellerIds.filter(id => id !== storytellerId)
        : [...prev.storytellerIds, storytellerId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert local time to UTC for storage
      // The date and time are in the user's selected timezone, not the browser's timezone
      const utcDateTime = userInputToUTC(formData.date, formData.time, formData.timezone);
      
      const eventData = {
        ...formData,
        date: utcDateTime,
        // Remove timezone from eventData - we'll always use UTC
        duration: parseInt(formData.duration) * 60, // Convert hours to minutes
        createDiscordEvent: formData.createDiscordEvent
      };

      const response = await axios.post('/api/events', eventData);
      
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      console.error('❌ [CREATE-EVENT] Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
              <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Event</h1>
          <p className="text-gray-600 dark:text-gray-400">Set up a new Blood on the Clocktower event</p>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Event Details</h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="input"
                placeholder="e.g., Friday Night BoTC"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="input"
                placeholder="Describe the event, any special rules, or additional information..."
              />
            </div>
          </div>
        </div>

        {/* Date and Time */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Date & Time</h2>
          
          {/* Quick Select Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quick Select (8:30 PM ET)
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleQuickSelect(3, 'Wednesday')}
                className="px-3 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                title={`Next Wednesday: ${formatDateForDisplay(3)}`}
              >
                Next Wednesday
              </button>
              <button
                type="button"
                onClick={() => handleQuickSelect(5, 'Friday')}
                className="px-3 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                title={`Next Friday: ${formatDateForDisplay(5)}`}
              >
                Next Friday
              </button>
              <button
                type="button"
                onClick={() => handleQuickSelect(6, 'Saturday')}
                className="px-3 py-2 text-sm bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
                title={`Next Saturday: ${formatDateForDisplay(6)}`}
              >
                Next Saturday
              </button>
              <button
                type="button"
                onClick={handleTodaySelect}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
              >
                Today
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Hover over buttons to see the exact date
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date *
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Time *
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                required
                className="input"
              />
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone *
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="input"
              >
                <option value="America/New_York">🇺🇸 Eastern Time (ET) - Default</option>
                <option value="America/Chicago">🇺🇸 Central Time (CT)</option>
                <option value="America/Denver">🇺🇸 Mountain Time (MT)</option>
                <option value="America/Los_Angeles">🇺🇸 Pacific Time (PT)</option>
                <option value="UTC">🌐 UTC</option>
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Events are created in Eastern Time by default. The timezone display below shows how this will appear in different timezones.
              </p>
            </div>

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (hours)
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                min="1"
                max="8"
                step="0.5"
                className="input"
              />
            </div>
          </div>
          
          {/* Timezone Display */}
          <TimezoneDisplay 
            date={formData.date} 
            time={formData.time} 
            timezone={formData.timezone} 
          />
        </div>

        {/* Location and Players */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Location & Players</h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="input"
                placeholder="e.g., Discord Voice Channel #general"
              />
            </div>
          </div>
        </div>

        {/* Storytellers */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Storytellers</h2>
          
          {storytellers.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">No storytellers available</p>
          ) : (
            <div className="space-y-3">
              {storytellers.map((storyteller) => (
                <label key={storyteller.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.storytellerIds.includes(storyteller.id)}
                    onChange={() => handleStorytellerToggle(storyteller.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex items-center space-x-3 flex-1">
                    {storyteller.avatar && storyteller.discord_id && (
                      <img
                        src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                        alt={storyteller.username}
                        className="w-8 h-8 rounded-full"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {storyteller.username}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {storyteller.role}
                      </span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Discord Integration */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Discord Integration</h2>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="createDiscordEvent"
                name="createDiscordEvent"
                checked={formData.createDiscordEvent}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="createDiscordEvent" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Create Discord Event
              </label>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400">
              When enabled, this will automatically create a Discord event in your server's events channel. 
              Users can then respond to the Discord event, and their responses will be synced with the webapp.
            </p>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <X size={16} />
            <span>Cancel</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="btn btn-secondary flex items-center space-x-2"
            title="Go to Admin panel to create template events"
          >
            <Calendar size={16} />
            <span>Use Templates</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={16} />
            <span>{loading ? 'Creating...' : 'Create Event'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEvent; 